'use client'

/**
 * One shared poller per endpoint for shell data (story clock, ticker, nav
 * badges). Every component that reads the same resource shares one request
 * loop; the loop runs only while something is subscribed and skips a tick
 * while the tab is hidden.
 */

import { useSyncExternalStore } from 'react'
import { apiClient, apiErrorStatus } from '@/lib/api-client'

export interface PolledSnapshot<T> {
  data: T | null
  /** HTTP status of the last failure (for example 501 while a lane has not built the endpoint), else null. */
  errorStatus: number | null
  error: string | null
  loading: boolean
}

export interface PolledResource<T> {
  useResource: () => PolledSnapshot<T>
  /** Fetch now. A refetch supersedes a request already in flight, so a persona change never shows the old answer. */
  refetch: () => Promise<void>
  /** Fetch once after `delayMs`; every call in the meantime joins the same request (a burst of frames makes one fetch). */
  refetchSoon: (delayMs?: number) => void
  /** Change the poll interval; a running loop restarts at the new pace. */
  setIntervalMs: (ms: number) => void
}

const REFETCH_SOON_MS = 400

export function createPolledResource<T>(endpoint: string, intervalMs: number): PolledResource<T> {
  const initial: PolledSnapshot<T> = { data: null, errorStatus: null, error: null, loading: true }
  let snapshot = initial
  let timer: ReturnType<typeof setInterval> | null = null
  let inflight: Promise<void> | null = null
  // Each request carries a generation; a forced refetch (persona change) supersedes older answers.
  let generation = 0
  let lastBody = ''
  let subscribers = 0
  let pollMs = intervalMs
  const listeners = new Set<() => void>()

  const emit = () => listeners.forEach((listener) => listener())

  const load = (force = false): Promise<void> => {
    if (inflight && !force) return inflight
    const mine = ++generation
    const request: Promise<void> = (async () => {
      try {
        const data = await apiClient.get<T>(endpoint)
        if (mine !== generation) return
        const body = JSON.stringify(data)
        // Same answer as last time: keep the snapshot, so subscribers do not re-render.
        if (body === lastBody && snapshot.errorStatus === null && !snapshot.loading) return
        lastBody = body
        snapshot = { data, errorStatus: null, error: null, loading: false }
        emit()
      } catch (err) {
        if (mine !== generation) return
        snapshot = {
          data: snapshot.data,
          errorStatus: apiErrorStatus(err),
          error: err instanceof Error ? err.message : 'Could not load',
          loading: false,
        }
        emit()
      } finally {
        if (generation === mine) inflight = null
      }
    })()
    inflight = request
    return request
  }

  const tick = () => {
    if (typeof document !== 'undefined' && document.hidden) return
    void load()
  }

  const subscribe = (listener: () => void) => {
    listeners.add(listener)
    subscribers += 1
    if (subscribers === 1) {
      void load()
      timer = setInterval(tick, pollMs)
    }
    return () => {
      listeners.delete(listener)
      subscribers -= 1
      if (subscribers === 0 && timer) {
        clearInterval(timer)
        timer = null
      }
    }
  }

  const getSnapshot = () => snapshot
  const getServerSnapshot = () => initial
  let soonTimer: ReturnType<typeof setTimeout> | null = null

  return {
    useResource: () => useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot),
    refetch: () => load(true),
    refetchSoon: (delayMs = REFETCH_SOON_MS) => {
      if (soonTimer) return
      soonTimer = setTimeout(() => {
        soonTimer = null
        void load(true)
      }, delayMs)
    },
    setIntervalMs: (ms: number) => {
      if (ms === pollMs) return
      pollMs = ms
      if (!timer) return
      clearInterval(timer)
      timer = setInterval(tick, pollMs)
    },
  }
}
